import styles from './Dashboard.module.scss';
import { Text } from '../../components/typography';
import QuickAction from './QuickAction';
import { Spinner } from 'react-bootstrap';
import ActionItemSlimCard from './ActionItemSlimCard';
import SafetyShareCard from './SafetyShareCard';
import { useActionItemModal } from '../../hooks/misc';
import InspectionCard from '../../components/inspections/inspection_items/InspectionCard';
import { useNavigate } from 'react-router-dom';
import {
  allStatuses,
  allTypesOption,
  customTaskAssessmentType,
  paths,
  quickActionTypes,
} from '../../constants/strings';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { getRoute } from '../../constants/strings';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import CustomQuickAction from './CustomQuickAction';
import { announcementIcons } from '../../constants/strings';
import { modals } from '../../providers/modals';
import { useModal } from '../../providers/ModalProvider';
import DashboardCard from './desktop/DashboardCard';
import { useCurrentUser } from '../../providers/UserProvider';
import moment from 'moment';
import party from '../../assets/empty_state_images/party_actions.svg';
import Spacer from '../../components/typography/Spacer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useOpenObservationModal } from '../../hooks/useOpenObservationModal';

export default function Dashboard({
  tasks = [],
  actionItems = [],
  inspections = [],
  loading = () => {},
  quickActions = [],
  customQuickActions = [],
  editableQuickAction = [],
  refetch = () => {},
  isEditing = false,
  announcements = [],
  setAnnouncementsExpanded,
  announcementExpanded,
  hideShowAll = false,
}) {
  const { user, assessmentTypes } = useCurrentUser();
  const { workspaceId, workspace, observationTemplate } = useWorkspace();
  const { openActionItemModal } = useActionItemModal();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { openObservationModal } = useOpenObservationModal();
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const now = moment();
  const { simpleMobileDashboard } = useFlags();

  const emptyState = (
    <div className={styles.emptyStateContainer}>
      <img src={party} alt="party" className={styles.emptyStateImage} />
      <Text noMargin size="sm" color="secondary" weight="semiBold">
        {t(keys.dashboard.ACTION_ITEMS_EMPTY_STATE_TITLE)}
      </Text>
    </div>
  );

  return (
    <div className={styles.container}>
      <Text size="lg" weight="bold" noMargin>
        {`${user.firstName ? `${user.firstName}'s` : ''} ${t(
          keys.dashboard.DASHBOARD,
        )}`}
      </Text>
      <Spacer />
      {simpleMobileDashboard ? (
        <div className={styles.quickActionsContainer}>
          {quickActions.map(
            ({ title, color, icon, onClick, disabled, className }, idx) => (
              <QuickAction
                fullWidth
                textSize="lg"
                key={title + icon + idx}
                title={title}
                icon={icon}
                color={color}
                onClick={onClick}
                disabled={disabled}
                className={className}
              />
            ),
          )}
        </div>
      ) : (
        <div className={styles.quickActionsContainer}>
          {quickActions.map(
            ({ title, color, icon, onClick, disabled, className }, idx) => (
              <QuickAction
                key={title + icon + idx}
                title={title}
                icon={icon}
                color={color}
                onClick={onClick}
                disabled={disabled}
                className={className}
              />
            ),
          )}
          {customQuickActions?.map((quickAction, index) => (
            <CustomQuickAction
              key={`${quickAction?.id}-${index}`}
              quickAction={quickAction}
              refetch={refetch}
              isEditing={
                isEditing && quickAction.type === quickActionTypes.userLevel
              }
            />
          ))}
          {editableQuickAction.map(
            ({ title, color, icon, onClick, disabled, className }, idx) => (
              <QuickAction
                key={title + idx + icon}
                title={title}
                icon={icon}
                color={color}
                onClick={onClick}
                disabled={disabled}
                className={className}
              />
            ),
          )}
        </div>
      )}
      <Spacer />
      {!simpleMobileDashboard && <SafetyShareCard />}
      <br />
      {!simpleMobileDashboard && (
        <>
          <div className={styles.categoryHeader}>
            <Text weight="bold" size="md-lg" noMargin>
              {t(keys.tasks.MY_TASKS)}
            </Text>
          </div>
          <div className={styles.actionItemsContainer}>
            {loading ? (
              <div className={styles.spinnerContainer}>
                <Spinner variant="primary" />
              </div>
            ) : tasks.length > 0 ? (
              tasks?.map((task) => (
                <DashboardCard
                  workspace={task?.workspace?.id}
                  status={{
                    name:
                      t(allStatuses[task?.status]?.title) ||
                      allStatuses[task?.status]?.title,
                    color: allStatuses[task?.status]?.color,
                  }}
                  key={task?.id}
                  header={task?.title}
                  subtext={`Due ${
                    task.interval === 'WEEKLY'
                      ? now.clone().day(7).fromNow()
                      : task.interval === 'MONTHLY'
                      ? now.clone().add(1, 'month').startOf('month').fromNow()
                      : 'today'
                  }`}
                  icon={
                    { ...assessmentTypes, ...customTaskAssessmentType }[
                      task?.template?.templateType.toLowerCase()
                    ]?.icon || 'content_paste_search'
                  }
                  iconColor={
                    { ...assessmentTypes, ...customTaskAssessmentType }[
                      task?.template?.templateType.toLowerCase()
                    ]?.color || 'blue'
                  }
                  onClick={() => {
                    if (task.taskType === 'INSPECTION') {
                      openModal({
                        modalName: modals.createAssessment,
                        variables: {
                          dataWorkspaceId: task?.workspace?.id,
                          allWorkspaces: !task.workspace?.id,
                          participants: [user],
                          type:
                            {
                              ...assessmentTypes,
                              ...customTaskAssessmentType,
                            }[task.template.templateType.toLowerCase()] ||
                            allTypesOption,
                          template: task.template,
                        },
                      });
                    } else if (task.taskType === 'OBSERVATION') {
                      openObservationModal({
                        targetWorkspaceId: workspaceId,
                        fallbackTemplate: observationTemplate,
                        variables: {
                          participants: [],
                          workspaceId,
                          dateTime: new moment().format('YYYY-MM-DD HH:mm'),
                        },
                      });
                    } else {
                      const vehicle =
                        (
                          task.assigneeInfo?.find(
                            (a) => a.assignee?.id === user?.id,
                          ) ?? task.assigneeInfo?.[0]
                        )?.vehicle ?? null;
                      openModal({
                        modalName: modals.createAssessment,
                        variables: {
                          dataWorkspaceId: task.workspace?.id ?? null,
                          allWorkspaces: !task.workspace?.id,
                          participants: [user],
                          template: task.template,
                          type:
                            {
                              ...assessmentTypes,
                              ...customTaskAssessmentType,
                            }[task.template?.templateType?.toLowerCase()] ||
                            allTypesOption,
                          asset: vehicle,
                        },
                      });
                    }
                  }}
                />
              ))
            ) : (
              emptyState
            )}
          </div>
          <br />
          <div className={styles.categoryHeader}>
            <Text weight="bold" noMargin size="md-lg">
              My Action Items
            </Text>
            <Text
              className={styles.link}
              onClick={() =>
                navigate(getRoute(workspace?.id, paths.actionItems))
              }
              size="sm"
              weight="semiBold"
              noMargin
              color="accentPrimary"
            >
              {t(keys.dashboard.SEE_ALL)}
            </Text>
          </div>
          <div className={styles.actionItemsContainer}>
            {loading ? (
              <div className={styles.spinnerContainer}>
                <Spinner variant="primary" />
              </div>
            ) : actionItems.length > 0 ? (
              actionItems?.map(
                ({
                  id,
                  title,
                  priority,
                  dueDate,
                  creator,
                  type,
                  incident,
                  isCorrective,
                  cpaId,
                  status,
                }) => {
                  return (
                    <ActionItemSlimCard
                      key={id}
                      title={title}
                      priority={priority}
                      dueDate={dueDate}
                      creator={creator}
                      status={status}
                      type={type}
                      onClick={() => {
                        if (isCorrective) {
                          navigate(`/${paths.cpa}/${cpaId}`);
                        }
                        if (type === 'INCIDENT_COMMENT') {
                          navigate(
                            `/${paths.incident}/${incident.id}?section=REVIEW`,
                          );
                        } else {
                          openActionItemModal(id, null);
                        }
                      }}
                      disabled={!online}
                    />
                  );
                },
              )
            ) : (
              emptyState
            )}
          </div>
          <br />
        </>
      )}
      <div className={styles.categoryHeader}>
        <Text weight="bold" noMargin size="md-lg">
          {t(keys.dashboard.RECENT_ASSESSMENTS)}
        </Text>
        <Text
          size="sm"
          weight="semiBold"
          noMargin
          color="accentPrimary"
          className={styles.link}
          onClick={() => navigate(getRoute(workspace?.id, paths.assessments))}
        >
          {t(keys.dashboard.SEE_ALL)}
        </Text>
      </div>
      <div className={styles.inspectionContainer}>
        {inspections.length > 0
          ? inspections?.map((inspection) => (
              <InspectionCard
                key={`inspection:${inspection.id}`}
                inspection={inspection}
                onClick={() =>
                  navigate(
                    getRoute(
                      inspection?.workspaceId || workspaceId,
                      paths.assessment,
                      inspection.id,
                    ),
                  )
                }
              />
            ))
          : emptyState}
      </div>
      <br />
      <div className={styles.categoryHeader}>
        <Text size="md-lg" weight="bold" noMargin>
          {t(keys.dashboard.RECENT_ANNOUNCEMENTS)}
        </Text>
        {!hideShowAll && (
          <Text
            size="sm"
            weight="semiBold"
            noMargin
            color="accentPrimary"
            className={styles.link}
            onClick={() => setAnnouncementsExpanded(!announcementExpanded)}
          >
            {announcementExpanded
              ? t(keys.dashboard.SHOW_LESS)
              : t(keys.dashboard.SHOW_MORE)}
          </Text>
        )}
      </div>
      <div className={styles.inspectionContainer}>
        {announcements.length > 0
          ? announcements?.map(
              ({ dateReleased, status, announcement, id, signatureUrl }) => (
                <DashboardCard
                  rightContent={
                    <img
                      className={styles.avatar}
                      src={announcement?.creator.avatarUrl}
                      alt={announcement?.creator.firstName}
                    />
                  }
                  header={announcement.title}
                  subtext={dateReleased}
                  key={`announcement:${announcement.id}`}
                  icon={announcementIcons[status].icon}
                  iconColor={announcementIcons[status].color}
                  onClick={() => {
                    openModal({
                      modalName: modals.announcement,
                      variables: {
                        announcement,
                        status,
                        id,
                        signatureUrl,
                      },
                    });
                  }}
                />
              ),
            )
          : emptyState}
      </div>
    </div>
  );
}
